import type { ReferentialsResponseDto } from '@/api/WhisApi/generated/model/referentialsResponseDto';
import { buildToggleOptions } from '@/screens/ReportCreation/forms/form.utils';
import {
  buildFamilyOptionsBySubcategory,
  buildSampleTypeOptionsByAnimalType,
  buildSubcategoryOptionsByCategory,
} from '@/screens/ReportCreation/forms/referentialOptions';
import type {
  ReportFormData,
  FormField,
  FormLayout,
  FormOption,
  SelectField,
  TextAreaField,
  ToggleGroupField,
} from '@/screens/ReportCreation/reportCreation.types';
import type { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import { ABSOLUTE_DEPENDENCY_PREFIX } from '../utils/conditionalVisibility.utils';

interface Step3FormConfigOptions {
  referentials?: ReferentialsResponseDto;
  includeSubcategoryField?: boolean;
  includeAllFamilyCategories?: boolean;
}

const buildStep3FormConfig = (
  t: TFunction,
  language: SupportedLanguage,
  {
    referentials,
    includeSubcategoryField = false,
    includeAllFamilyCategories = false,
  }: Step3FormConfigOptions,
): FormLayout => {
  const sampleTypeOptionsByAnimalType = buildSampleTypeOptionsByAnimalType(
    referentials?.sampleTypes,
    language,
  );

  const subcategoryOptionsByCategory = buildSubcategoryOptionsByCategory(
    referentials?.subCategories,
    language,
  );

  const familyOptionsBySubcategory = buildFamilyOptionsBySubcategory(
    referentials?.families,
    language,
  );

  const categoryKeys = ['direct', 'indirect', 'observation', 'others'];

  const categoriesWithSubcategoryOptions = includeSubcategoryField
    ? categoryKeys
    : categoryKeys.filter(
        (category) => (subcategoryOptionsByCategory[category] ?? []).length > 0,
      );

  const categoriesWithFamilyOptions = includeAllFamilyCategories
    ? categoryKeys
    : categoryKeys.filter((category) => {
        const subcategories = subcategoryOptionsByCategory[category] ?? [];
        return subcategories.some(
          (subcategory) =>
            (familyOptionsBySubcategory[subcategory.value] ?? []).length > 0,
        );
      });

  const subcategoryOptions: FormOption[] = [];

  const sampleOptions = sampleTypeOptionsByAnimalType;

  const categoryOptions = buildToggleOptions(t, [
    {
      value: 'direct',
      labelKey: 'reportCreation.step3.options.category.direct',
    },
    {
      value: 'indirect',
      labelKey: 'reportCreation.step3.options.category.indirect',
    },
    {
      value: 'observation',
      labelKey: 'reportCreation.step3.options.category.observation',
    },
    {
      value: 'others',
      labelKey: 'reportCreation.step3.options.category.others',
    },
  ], language);

  const hasSubcategoryOptions = Object.values(
    subcategoryOptionsByCategory,
  ).some((options) => options.length > 0);
  const shouldIncludeSubcategoryField =
    includeSubcategoryField || hasSubcategoryOptions;

  const categoryField: ToggleGroupField<ReportFormData> = {
    id: 'tests.category',
    type: 'toggle-group',
    label: t('reportCreation.step3.fields.category', { lng: language }),
    required: true,
    options: categoryOptions,
  };

  const subcategoryField: SelectField<ReportFormData> = {
    id: 'tests.subcategory',
    type: 'select',
    label: t('reportCreation.step3.fields.subcategory', { lng: language }),
    placeholder: t('reportCreation.step3.placeholders.subcategory', { lng: language }),
    required: true,
    options: subcategoryOptions,
    dynamicOptions: {
      dependsOn: 'tests.category',
      optionsByValue: subcategoryOptionsByCategory,
    },
    conditional: {
      dependsOn: 'tests.category',
      visibleWhen: categoriesWithSubcategoryOptions,
    },
  };

  const familyField: SelectField<ReportFormData> = {
    id: 'tests.family',
    type: 'select',
    label: t('reportCreation.step3.fields.family', { lng: language }),
    placeholder: t('reportCreation.step3.placeholders.family', { lng: language }),
    required: true,
    options: [],
    dynamicOptions: {
      dependsOn: 'tests.subcategory',
      optionsByValue: familyOptionsBySubcategory,
    },
    conditional: {
      dependsOn: 'tests.category',
      visibleWhen: categoriesWithFamilyOptions,
    },
  };

  const extraInfoField: TextAreaField<ReportFormData> = {
    id: 'tests.extraInfo',
    label: t('reportCreation.step3.fields.extraTestInfo', { lng: language }),
    type: 'textarea',
    placeholder: t('reportCreation.step3.placeholders.extraTestInfo', { lng: language }),
    required: false,
    maxLength: 5000,
  };

  const testFields: FormField<ReportFormData>[] = [
    categoryField,
    ...(shouldIncludeSubcategoryField ? [subcategoryField] : []),
    familyField,
    extraInfoField,
  ];

  return {
    repeatableSections: [
      {
        id: 'diagnosticProcedures',
        title: t('reportCreation.step3.diagnosticProcedureTitle', { lng: language }),
        addButtonSectionTitle: t(
          'reportCreation.step3.actions.addButtonSectionTitle',
          { lng: language },
        ),
        addButtonLabel: t('reportCreation.step3.actions.addProcedure', { lng: language }),
        minInstances: 1,
        sections: [
          {
            title: t('reportCreation.step3.sections.sampling', { lng: language }),
            fields: [
              {
                id: 'sampling.sampleType',
                type: 'select',
                label: t('reportCreation.step3.fields.sampleType', { lng: language }),
                placeholder: t('reportCreation.step3.placeholders.sampleType', { lng: language }),
                required: true,
                options: [],
                dynamicOptions: {
                  dependsOn: `${ABSOLUTE_DEPENDENCY_PREFIX}animalType`,
                  optionsByValue: sampleOptions,
                },
              },
            ],
          },
          {
            title: t('reportCreation.step3.sections.tests', { lng: language }),
            fields: [...testFields],
          },
          {
            title: t('reportCreation.step3.sections.results', { lng: language }),
            fields: [
              {
                id: 'results.positiveCount',
                type: 'numeric',
                label: t('reportCreation.step3.fields.positiveCount', { lng: language }),
                required: false,
              },
              {
                id: 'results.negativeCount',
                type: 'numeric',
                label: t('reportCreation.step3.fields.negativeCount', { lng: language }),
                required: false,
              },
              {
                id: 'results.totalCount',
                type: 'numeric',
                label: t('reportCreation.step3.fields.totalCount', { lng: language }),
                required: false,
              },
            ],
          },
        ],
      },
    ],
  };
};

export const getStep3FormConfig = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): FormLayout => buildStep3FormConfig(t, language, { referentials });

export const getStep3DetailFormConfig = (
  t: TFunction,
  language: SupportedLanguage,
): FormLayout =>
  buildStep3FormConfig(t, language, {
    includeSubcategoryField: true,
    includeAllFamilyCategories: true,
  });
